import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { BroadcastIcon, CalendarPlusIcon, LightningIcon } from "@phosphor-icons/react";

export function StartBatchDialog({ campaign, onClose, onStartNow, onSchedule, busy }) {
    const open = !!campaign;
    const bh = campaign?.business_hours || {};
    const tz = bh.timezone || "America/New_York";
    const startHour = bh.start_hour ?? 9;
    const endHour = bh.end_hour ?? 17;
    const weekdaysOnly = bh.weekdays_only !== false;

    const [now, setNow] = React.useState(new Date());
    React.useEffect(() => {
        if (!open) return undefined;
        const i = setInterval(() => setNow(new Date()), 15000);
        return () => clearInterval(i);
    }, [open]);

    let localStr = "—";
    let isOpen = false;
    try {
        localStr = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(now);
        const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", hour12: false, weekday: "short" }).formatToParts(now);
        const hourNow = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
        const wk = parts.find((p) => p.type === "weekday")?.value || "Mon";
        const isWeekend = wk === "Sat" || wk === "Sun";
        isOpen = (!weekdaysOnly || !isWeekend) && hourNow >= startHour && hourNow < endHour;
    } catch { /* invalid tz */ }

    const nextOpenLabel = React.useMemo(() => {
        if (isOpen) return "now";
        try {
            const fmt = (d) => new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
            for (let i = 0; i < 8; i++) {
                const next = new Date(now);
                next.setDate(next.getDate() + i);
                const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, weekday: "short" }).formatToParts(next);
                const wk = parts.find((p) => p.type === "weekday")?.value;
                const isWeekend = wk === "Sat" || wk === "Sun";
                if (weekdaysOnly && isWeekend) continue;
                const ymd = new Intl.DateTimeFormat("en-US", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(next);
                const yr = ymd.find((p) => p.type === "year").value;
                const mo = ymd.find((p) => p.type === "month").value;
                const da = ymd.find((p) => p.type === "day").value;
                const openLocalISO = `${yr}-${mo}-${da}T${String(startHour).padStart(2, "0")}:00:00`;
                const opener = new Date(new Date(openLocalISO).toLocaleString("en-US", { timeZone: tz }));
                const guessUTC = new Date(openLocalISO + "Z");
                const offset = opener.getTime() - guessUTC.getTime();
                const candidate = new Date(guessUTC.getTime() - offset);
                if (candidate > now) return fmt(candidate);
            }
        } catch { /* */ }
        return "next business window";
    }, [now, tz, startHour, weekdaysOnly, isOpen]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-[2px] sm:max-w-[520px]" data-testid="start-batch-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BroadcastIcon size={18} weight="bold" /> Start batch — {campaign?.name}
                    </DialogTitle>
                    <DialogDescription>
                        Pick how to dial the next 5 prospects in this campaign.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2 text-[12px]">
                    <div className="px-4 py-3 border border-[var(--border)] bg-[var(--surface-active)]" data-testid="start-batch-window-info">
                        <div className="overline text-[10px] mb-1.5">CAMPAIGN WINDOW</div>
                        <div className="mono text-[13px]">
                            {tz} · {String(startHour).padStart(2, "0")}:00–{String(endHour).padStart(2, "0")}:00 · {weekdaysOnly ? "Mon–Fri" : "All days"}
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-[11px]">
                            <span className="text-[var(--text-secondary)]">Local now:</span>
                            <span className="mono font-semibold">{localStr}</span>
                            <span
                                className="overline text-[9px] px-1.5 py-0.5"
                                style={{ borderWidth: "1px", borderStyle: "solid", borderColor: isOpen ? "var(--success)" : "var(--warning)", color: isOpen ? "var(--success)" : "var(--warning)" }}
                                data-testid="window-status-pill"
                            >
                                {isOpen ? "OPEN" : "CLOSED"}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={onStartNow}
                        disabled={busy}
                        className="w-full px-4 py-3 border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--surface-active)] text-left transition-colors disabled:opacity-50"
                        data-testid="start-now-option"
                    >
                        <div className="flex items-center gap-2">
                            <LightningIcon size={16} weight="bold" className="text-[var(--accent)]" />
                            <span className="font-semibold text-[14px]">Start now</span>
                            {!isOpen && <span className="overline text-[9px] px-1.5 py-0.5 border border-[var(--warning)] text-[var(--warning)]">OVERRIDE</span>}
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                            {isOpen
                                ? "Dial the next 5 prospects immediately."
                                : "Override business hours and dial right away. You'll be asked to confirm."}
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={onSchedule}
                        disabled={busy}
                        className="w-full px-4 py-3 border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--surface-active)] text-left transition-colors disabled:opacity-50"
                        data-testid="schedule-option"
                    >
                        <div className="flex items-center gap-2">
                            <CalendarPlusIcon size={16} weight="bold" className="text-[#0EA5E9]" />
                            <span className="font-semibold text-[14px]">Schedule for campaign window</span>
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                            {isOpen
                                ? "Defer to the start of the next configured window."
                                : `Queue the batch to fire at the next opening — ${nextOpenLabel}.`}
                        </div>
                    </button>
                </div>

                <DialogFooter>
                    <button type="button" onClick={onClose} disabled={busy} className="btn-ghost px-4 py-2 text-[13px]" data-testid="start-batch-cancel">
                        Cancel
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
